import React, { useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Check, X, RefreshCw, Loader2, Ban,
  RotateCcw, Monitor, ChevronDown, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';

import { API_BASE as BASE } from '../lib/apiBase';

const eur = (n) =>
  (Number(n) || 0).toLocaleString('nl-NL', {
    style: 'currency',
    currency: 'EUR',
  });

const badge = (status, t) => {
  if (status === 'paid') {
    return { bg: '#dcfce7', c: '#166534', l: t('Betaald', 'Betaald') };
  }

  if (status === 'cancelled') {
    return { bg: '#fee2e2', c: '#991b1b', l: t('Geannuleerd', 'Geannuleerd') };
  }

  if (status === 'revoked') {
    return { bg: '#fee2e2', c: '#991b1b', l: t('Ingetrokken', 'Ingetrokken') };
  }

  return { bg: '#fef3c7', c: '#92400e', l: t('Wacht op betaling', 'Wacht op betaling') };
};

export default function AdminOrders() {
  const { t } = useT();

  const [key, setKey] = useState(sessionStorage.getItem('mc_admin_key') || '');
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [activity, setActivity] = useState({});
  const [view, setView] = useState('orders');
  const [basics, setBasics] = useState([]);
  const [basicsLoaded, setBasicsLoaded] = useState(false);

  const hdr = (k) => ({ headers: { 'X-Admin-Key': k } });

  const loadBasics = async (k = key) => {
    try {
      const r = await axios.get(`${BASE}/api/webshop/admin/basic-accounts`, hdr(k));
      setBasics(r.data);
      setBasicsLoaded(true);
    } catch (e) {
      toast.error(t('Kon gratis accounts niet laden', 'Kon gratis accounts niet laden'), {
        description: e.response?.data?.detail || e.message,
      });
    }
  };

  const load = async (k = key) => {
    try {
      const r = await axios.get(`${BASE}/api/webshop/admin/orders`, hdr(k));
      setOrders(r.data);
      setAuthed(true);
      sessionStorage.setItem('mc_admin_key', k);
    } catch (e) {
      setAuthed(false);
      toast.error(t('Geen toegang', 'Geen toegang'), {
        description: e.response?.data?.detail || e.message,
      });
    }
  };

  const approve = async (ref) => {
    if (!window.confirm(t(
      'Betaling van bestelling ontvangen? De licentie wordt direct gegenereerd en gemaild.',
      `Betaling van bestelling ${ref} ontvangen? De licentie wordt direct gegenereerd en gemaild.`
    ))) return;

    setBusy(ref);

    try {
      const r = await axios.post(`${BASE}/api/webshop/admin/orders/${ref}/approve`, {}, hdr(key));
      toast.success(`${t('Licentie verstuurd voor', 'Licentie verstuurd voor')} ${ref}`, {
        description: `${r.data.license_key?.slice(0, 40)}...`,
      });
      load();
    } catch (e) {
      toast.error(t('Mislukt', 'Mislukt'), {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setBusy('');
    }
  };

  const cancel = async (ref) => {
    if (!window.confirm(`${t('Bestelling annuleren?', 'Bestelling annuleren?')} ${ref}`)) return;

    setBusy(ref);

    try {
      await axios.post(`${BASE}/api/webshop/admin/orders/${ref}/cancel`, {}, hdr(key));
      toast.success(`${t('Bestelling geannuleerd', 'Bestelling geannuleerd')}: ${ref}`);
      load();
    } catch (e) {
      toast.error(t('Mislukt', 'Mislukt'), {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setBusy('');
    }
  };

  const revoke = async (ref) => {
    const reason = window.prompt(
      `${t('Reden voor het intrekken van licentie', 'Reden voor het intrekken van licentie')} ${ref}? ${t('(wordt vastgelegd in het logboek)', '(wordt vastgelegd in het logboek)')}`,
      ''
    );

    if (reason === null) return;

    const notify = window.confirm(
      t(
        'Wil je de garage per e-mail op de hoogte stellen van de intrekking? OK = wel mailen, Annuleren = niet mailen',
        'Wil je de garage per e-mail op de hoogte stellen van de intrekking (incl. reden)?\n\nOK = wel mailen · Annuleren = niet mailen'
      )
    );

    setBusy(ref);

    try {
      const r = await axios.post(`${BASE}/api/webshop/admin/orders/${ref}/revoke`, { reason, notify }, hdr(key));
      toast.success(`${t('Licentie ingetrokken', 'Licentie ingetrokken')}: ${ref}`, {
        description: r.data.emailed
          ? t('Garage is per e-mail geïnformeerd', 'Garage is per e-mail geïnformeerd')
          : t('Geen e-mail verstuurd', 'Geen e-mail verstuurd'),
      });
      load();
    } catch (e) {
      toast.error(t('Mislukt', 'Mislukt'), {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setBusy('');
    }
  };

  const restore = async (ref) => {
    if (!window.confirm(`${t('Licentie herstellen en weer geldig maken?', 'Licentie herstellen en weer geldig maken?')} ${ref}`)) return;

    setBusy(ref);

    try {
      await axios.post(`${BASE}/api/webshop/admin/orders/${ref}/restore`, {}, hdr(key));
      toast.success(`${t('Licentie hersteld', 'Licentie hersteld')}: ${ref}`);
      load();
    } catch (e) {
      toast.error(t('Mislukt', 'Mislukt'), {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setBusy('');
    }
  };

  const toggleActivity = async (ref) => {
    if (expanded === ref) {
      setExpanded(null);
      return;
    }

    setExpanded(ref);

    if (!activity[ref]) {
      try {
        const r = await axios.get(`${BASE}/api/webshop/admin/orders/${ref}/devices`, hdr(key));
        setActivity(a => ({ ...a, [ref]: r.data }));
      } catch (e) {
        toast.error(t('Kon activiteit niet laden', 'Kon activiteit niet laden'), {
          description: e.response?.data?.detail || e.message,
        });
      }
    }
  };

  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', background: '#f3f6fb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 400, width: '100%', boxShadow: '0 10px 30px rgba(15,23,42,.1)' }} data-testid="admin-login-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <ShieldCheck size={20} color="#2563eb" />
            <h2 style={{ margin: 0, fontSize: 18 }}>{t('Bestellingen beheer', 'Bestellingen beheer')}</h2>
          </div>

          <p style={{ fontSize: 13, color: '#64748b' }}>
            {t('Voer de admin-sleutel in om bestellingen te beheren.', 'Voer de admin-sleutel in om bestellingen te beheren.')}
          </p>

          <input
            type="password"
            value={key}
            onChange={e => setKey(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
            placeholder={t('Admin-sleutel', 'Admin-sleutel')}
            data-testid="admin-key-input"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }}
          />

          <button
            onClick={() => load()}
            data-testid="admin-login-btn"
            style={{ width: '100%', padding: 11, border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
          >
            {t('Inloggen', 'Inloggen')}
          </button>
        </div>
      </div>
    );
  }

  const orderHeaders = [
    t('Kenmerk', 'Kenmerk'),
    t('Datum', 'Datum'),
    t('Pakket', 'Pakket'),
    t('Klanten', 'Klanten'),
    t('Bonnen', 'Bonnen'),
    t('E-mail', 'E-mail'),
    t('Bedrag', 'Bedrag'),
    t('Status', 'Status'),
    '',
  ];

  const basicHeaders = [
    t('Garage', 'Garage'),
    t('E-mail eigenaar', 'E-mail eigenaar'),
    t('Plan', 'Plan'),
    t('Klanten', 'Klanten'),
    t('Bonnen', 'Bonnen'),
    t('Aangemaakt', 'Aangemaakt'),
    t('Status', 'Status'),
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f3f6fb', padding: '32px 20px' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ display: 'flex', gap: 8, background: '#fff', padding: 4, borderRadius: 10, border: '1px solid #e2e8f0' }} data-testid="admin-tabs">
            <button
              onClick={() => setView('orders')}
              data-testid="admin-tab-orders"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600, background: view === 'orders' ? '#2563eb' : 'transparent', color: view === 'orders' ? '#fff' : '#475569' }}
            >
              <ShieldCheck size={14} /> {t('Bestellingen', 'Bestellingen')} ({orders.length})
            </button>

            <button
              onClick={() => { setView('basic'); if (!basicsLoaded) loadBasics(); }}
              data-testid="admin-tab-basic"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600, background: view === 'basic' ? '#2563eb' : 'transparent', color: view === 'basic' ? '#fff' : '#475569' }}
            >
              <Users size={14} /> {t('Gratis accounts', 'Gratis accounts')}{basicsLoaded ? ` (${basics.length})` : ''}
            </button>
          </div>

          <button
            onClick={() => view === 'orders' ? load() : loadBasics()}
            data-testid="admin-refresh-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13 }}
          >
            <RefreshCw size={14} /> {t('Verversen', 'Verversen')}
          </button>
        </div>

        {view === 'basic' ? (
          <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 6px rgba(15,23,42,.04)' }} data-testid="basic-accounts-table">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  {basicHeaders.map(h => (
                    <th key={h} style={{ padding: '10px 14px', color: '#64748b', fontWeight: 600, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {basics.map(a => (
                  <tr key={a.garageId} style={{ borderTop: '1px solid #f1f5f9' }} data-testid={`basic-row-${a.garageId}`}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{a.garageNaam || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <a href={`mailto:${a.email}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                        {a.email || '—'}
                      </a>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{a.plan}</span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>{a.klanten}</td>
                    <td style={{ padding: '10px 14px' }}>{a.bonnen}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{(a.createdAt || '').slice(0, 10)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {a.activated ? (
                        <span style={{ color: '#16a34a', fontSize: 12, fontWeight: 600 }}>
                          {t('Actief', 'Actief')}
                        </span>
                      ) : (
                        <span style={{ color: '#b45309', fontSize: 12, fontWeight: 600 }}>
                          {t('Niet geactiveerd', 'Niet geactiveerd')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}

                {basicsLoaded && basics.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                      {t('Geen gratis accounts gevonden.', 'Geen gratis accounts gevonden.')}
                    </td>
                  </tr>
                )}

                {!basicsLoaded && (
                  <tr>
                    <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                      <Loader2 size={14} className="spin" /> {t('Laden…', 'Laden…')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <p style={{ fontSize: 12, color: '#94a3b8', padding: '12px 16px', margin: 0 }}>
              {t(
                'Dit zijn garages zonder actieve betaalde licentie — potentiële upgrade-leads. Klik op een e-mailadres om contact op te nemen.',
                'Dit zijn garages zonder actieve betaalde licentie — potentiële upgrade-leads. Klik op een e-mailadres om contact op te nemen.'
              )}
            </p>
          </div>
        ) : (
          <>
            <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 6px rgba(15,23,42,.04)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    {orderHeaders.map(h => (
                      <th key={h || 'empty'} style={{ padding: '10px 14px', color: '#64748b', fontWeight: 600, fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {orders.map(o => {
                    const b = badge(o.status, t);
                    const act = activity[o.ref];

                    return (
                      <React.Fragment key={o.ref}>
                        <tr style={{ borderTop: '1px solid #f1f5f9' }} data-testid={`order-row-${o.ref}`}>
                          <td style={{ padding: '10px 14px', fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>{o.ref}</td>
                          <td style={{ padding: '10px 14px', color: '#64748b' }}>{(o.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                          <td style={{ padding: '10px 14px' }}>{o.label} <span style={{ color: '#94a3b8', fontSize: 11 }}>({o.plan})</span></td>
                          <td style={{ padding: '10px 14px' }} data-testid={`order-klanten-${o.ref}`}>{o.klanten ?? '—'}</td>
                          <td style={{ padding: '10px 14px' }} data-testid={`order-bonnen-${o.ref}`}>{o.bonnen ?? '—'}</td>
                          <td style={{ padding: '10px 14px' }}>{o.email}</td>
                          <td style={{ padding: '10px 14px', fontWeight: 700 }}>{eur(o.amount)}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <span style={{ background: b.bg, color: b.c, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{b.l}</span>
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {o.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => approve(o.ref)}
                                  disabled={busy === o.ref}
                                  data-testid={`approve-${o.ref}`}
                                  title={t('Betaling ontvangen → licentie automatisch genereren + mailen', 'Betaling ontvangen → licentie automatisch genereren + mailen')}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', border: 'none', borderRadius: 6, background: '#16a34a', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginRight: 6 }}
                                >
                                  {busy === o.ref ? <Loader2 size={12} className="spin" /> : <Check size={12} />} {t('Betaling ontvangen', 'Betaling ontvangen')}
                                </button>

                                <button
                                  onClick={() => cancel(o.ref)}
                                  disabled={busy === o.ref}
                                  data-testid={`cancel-${o.ref}`}
                                  title={t('Annuleren', 'Annuleren')}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', border: '1px solid #fecaca', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}
                                >
                                  <X size={12} />
                                </button>
                              </>
                            )}

                            {(o.status === 'paid' || o.status === 'revoked') && o.license_key && (
                              <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                <button
                                  onClick={() => toggleActivity(o.ref)}
                                  data-testid={`activity-${o.ref}`}
                                  title={t('Activatielogboek / apparaten', 'Activatielogboek / apparaten')}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#475569', cursor: 'pointer', fontSize: 12 }}
                                >
                                  <Monitor size={12} /> {t('Apparaten', 'Apparaten')} <ChevronDown size={12} style={{ transform: expanded === o.ref ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                                </button>

                                {o.status === 'paid' ? (
                                  <button
                                    onClick={() => revoke(o.ref)}
                                    disabled={busy === o.ref}
                                    data-testid={`revoke-${o.ref}`}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', border: '1px solid #fecaca', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                                  >
                                    {busy === o.ref ? <Loader2 size={12} className="spin" /> : <Ban size={12} />} {t('Intrekken', 'Intrekken')}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => restore(o.ref)}
                                    disabled={busy === o.ref}
                                    data-testid={`restore-${o.ref}`}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', border: '1px solid #bbf7d0', borderRadius: 6, background: '#fff', color: '#16a34a', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                                  >
                                    {busy === o.ref ? <Loader2 size={12} className="spin" /> : <RotateCcw size={12} />} {t('Herstellen', 'Herstellen')}
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>

                        {expanded === o.ref && (
                          <tr data-testid={`activity-row-${o.ref}`}>
                            <td colSpan={9} style={{ padding: '0 14px 14px', background: '#f8fafc' }}>
                              <div style={{ padding: '12px 14px', background: '#fff', borderRadius: 8, border: '1px solid #f1f5f9' }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
                                  {t('Activatielogboek', 'Activatielogboek')} {act ? `(${act.devices?.length || 0} ${t('apparaten', 'apparaten')})` : ''} {act?.garageId ? `· ${t('garage', 'garage')} ${act.garageId}` : ''}
                                </div>

                                {act?.revoked && act?.revokeReason && (
                                  <div style={{ fontSize: 12, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>
                                    <b>{t('Reden intrekking:', 'Reden intrekking:')}</b> {act.revokeReason}
                                  </div>
                                )}

                                {!act ? (
                                  <div style={{ color: '#94a3b8', fontSize: 12 }}>
                                    <Loader2 size={13} className="spin" /> {t('Laden…', 'Laden…')}
                                  </div>
                                ) : (act.devices || []).length === 0 ? (
                                  <div style={{ color: '#94a3b8', fontSize: 12 }}>
                                    {t('Nog geen activaties op deze licentie.', 'Nog geen activaties op deze licentie.')}
                                  </div>
                                ) : (
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                      <tr style={{ textAlign: 'left', color: '#94a3b8' }}>
                                        <th style={{ padding: '4px 8px' }}>{t('Apparaat / browser', 'Apparaat / browser')}</th>
                                        <th style={{ padding: '4px 8px' }}>{t('IP-adres', 'IP-adres')}</th>
                                        <th style={{ padding: '4px 8px' }}>{t('Eerste activatie', 'Eerste activatie')}</th>
                                        <th style={{ padding: '4px 8px' }}>{t('Laatste activiteit', 'Laatste activiteit')}</th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {act.devices.map(d => (
                                        <tr key={d.deviceId} style={{ borderTop: '1px solid #f1f5f9' }}>
                                          <td style={{ padding: '5px 8px', fontWeight: 600 }}>{d.deviceName || '—'}</td>
                                          <td style={{ padding: '5px 8px', fontFamily: 'ui-monospace, monospace' }}>{d.ip || '—'}</td>
                                          <td style={{ padding: '5px 8px', color: '#64748b' }}>{d.firstSeen ? new Date(d.firstSeen).toLocaleString('nl-NL') : '—'}</td>
                                          <td style={{ padding: '5px 8px', color: '#64748b' }}>{d.lastSeen ? new Date(d.lastSeen).toLocaleString('nl-NL') : '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {orders.length === 0 && (
                    <tr>
                      <td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                        {t('Nog geen bestellingen.', 'Nog geen bestellingen.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 14 }}>
              {t(
                'Klik op "Betaling ontvangen" zodra het bedrag met het juiste kenmerk op je rekening of PayPal staat. De licentiekey wordt dan automatisch gegenereerd en naar de klant gemaild.',
                'Klik op "Betaling ontvangen" zodra het bedrag (met het juiste kenmerk) op je rekening of PayPal staat. De licentiekey wordt dan automatisch gegenereerd en naar de klant gemaild.'
              )}
            </p>
          </>
        )}
      </div>

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}